import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api from "@/lib/api";
import { toast } from "sonner";
import { EnvelopeSimple, ChatCircleDots, Car, Signature as SigIcon, ReceiptX, HardHat, User, Warning } from "@phosphor-icons/react";
import { formatApiError } from "@/lib/api";

export default function ClientHistoryDialog({ open, onClose, clientId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !clientId) return;
    setLoading(true);
    api.get(`/clients/${clientId}/history`)
      .then((r) => setData(r.data))
      .catch((err) => toast.error(formatApiError(err)))
      .finally(() => setLoading(false));
  }, [open, clientId]);

  // Merge all events for a unified timeline
  const events = data ? [
    ...data.emails.map((e) => ({ kind: "email", ts: e.sent_at, ...e })),
    ...data.arrivals.map((a) => ({ kind: "arrival", ts: a.created_at, ...a })),
    ...data.signatures.map((s) => ({ kind: "signature", ts: s.created_at, ...s })),
    ...data.quote_events.map((q) => ({ kind: "quote", ts: q.sent_at || q.flagged_at, ...q })),
  ].sort((a, b) => (b.ts || "").localeCompare(a.ts || "")) : [];

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md bg-[#111] border-white/10 text-white rounded-2xl max-h-[90vh] overflow-y-auto" data-testid="client-history-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <User size={20} className="text-[#005CFF]" weight="duotone" />
            {data ? `Historique — ${data.client.name}` : "Historique client"}
          </DialogTitle>
        </DialogHeader>

        {loading && <div className="h-40 rounded-xl bg-white/5 animate-pulse" />}

        {data && (
          <div className="space-y-3">
            <div className="grid grid-cols-4 gap-1.5">
              <Stat count={data.chantiers.length} label="Chantiers" />
              <Stat count={data.emails.length} label="Emails" color="#005CFF" />
              <Stat count={data.arrivals.length} label="Arrivées" color="#10B981" />
              <Stat count={data.signatures.length} label="Signés" color="#F59E0B" />
            </div>

            {events.length === 0 ? (
              <div className="rounded-xl bg-white/[0.03] border border-dashed border-white/10 p-6 text-center">
                <Warning size={22} className="mx-auto text-white/40 mb-2" weight="duotone" />
                <div className="text-sm text-white/60">Aucune activité enregistrée</div>
              </div>
            ) : (
              <div className="space-y-1.5" data-testid="history-timeline">
                {events.map((ev, i) => <TimelineRow key={`ev-${i}-${ev.kind}-${ev.ts}`} ev={ev} />)}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Stat({ count, label, color = "#9CA3AF" }) {
  return (
    <div className="rounded-xl bg-white/5 border border-white/10 p-2 text-center">
      <div className="font-display font-bold text-lg" style={{ color }}>{count}</div>
      <div className="text-[9px] uppercase tracking-widest text-white/50 mt-0.5">{label}</div>
    </div>
  );
}

function TimelineRow({ ev }) {
  const meta = {
    email: { icon: EnvelopeSimple, color: "#005CFF", label: "Email envoyé" },
    arrival: { icon: Car, color: "#10B981", label: "Notification arrivée" },
    signature: { icon: SigIcon, color: "#F59E0B", label: "Signature" },
    quote: { icon: ReceiptX, color: "#EF4444", label: "Devis" },
  }[ev.kind] || { icon: User, color: "#9CA3AF", label: ev.kind };

  const title = ev.kind === "email" ? `À ${ev.recipient}` :
    ev.kind === "arrival" ? `ETA ${ev.eta_time} · ${[ev.email_ok && "email", ev.sms_ok && "SMS"].filter(Boolean).join(" + ") || "aucun canal"}` :
    ev.kind === "signature" ? `${ev.signer_type === "intervenant" ? "Intervenant" : "Client"} — ${ev.client_name}` :
    ev.kind === "quote" ? (ev.quote_sent ? `Envoyé à Axonaut${ev.axonaut_ref ? ` · ${ev.axonaut_ref}` : ""}` : "En attente devis") :
    "—";

  return (
    <div className="rounded-xl bg-white/[0.03] border border-white/10 p-2.5 flex items-start gap-2.5" data-testid={`history-${ev.kind}`}>
      <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${meta.color}22` }}>
        <meta.icon size={14} weight="duotone" color={meta.color} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-[10px] uppercase tracking-widest" style={{ color: meta.color }}>{meta.label}</span>
          {ev.chantier_name && <span className="text-[10px] text-white/40">· {ev.chantier_name}</span>}
        </div>
        <div className="text-xs text-white/80 mt-0.5 truncate">{title}</div>
        {ev.subject && <div className="text-[10px] text-white/50 truncate">{ev.subject}</div>}
        {ev.message && <div className="text-[10px] text-white/50 line-clamp-1">{ev.message}</div>}
        <div className="text-[9px] uppercase tracking-widest text-white/40 mt-0.5">
          {ev.ts ? new Date(ev.ts).toLocaleString("fr-FR") : "—"}
          {ev.sent_by_name && ` · ${ev.sent_by_name}`}
        </div>
      </div>
    </div>
  );
}
